import {ActionMenuContext} from '#/components/actionmenu/action-menu';
import {ActionMenuInfoTile} from '#/components/actionmenu/action-menu-info-tile';
import {ActionMenuLayoutContentNamed, ActionMenuLayoutFooter} from '#/components/actionmenu/action-menu-layout';
import {actionMenuMessageContent, useMessageCasesToContent} from '#/components/actionmenu/action-menu-message-content';
import {
  ActionMenuContentSectionOverviewLimitSell,
  ActionMenuContentSectionOverviewSell,
} from '#/components/actionmenu/content/action-menu-content-section-overview';
import {
  ActionMenuTradeMarketLimitTabs,
  LimitOrderTokenInputs,
  usePriceAmountSubtotal,
} from '#/components/actionmenu/sections/trade/action-menu-trade-limit';
import {TransactionVariables} from '#/components/actionmenu/sections/transaction/transaction-flow-data';
import {ActionMenuFormContainer, ActionMenuTransactionReviewButton} from '#/components/actionmenu/subcomponents';
import {ActionMenuTokenInput} from '#/components/actionmenu/token-input/action-menu-token-input';
import {useActionMenuTokenInputData} from '#/components/actionmenu/token-input/use-action-menu-token-input-data';
import {MessageCase} from '#/components/actionmenu/types';
import {maxSlippageDefaultPercentage} from '#/data/max-slippage-constants';
import {useDismissableInfoTile} from '#/hooks/use-dismisable-info-tile';
import {useQueryParamsTabKey} from '#/hooks/use-query-params-tab';
import {TradeTab} from '#/navigation/tabs';
import {MessageContent, TokenType} from '#/repository/types';
import {minimumTransactionFees} from '#/utils/contract-consts';
import {getTransactionFees} from '#/utils/get-transaction-fees';
import {percentageToRatio} from '#/utils/ratio-percentage';
import {DefaultChildren} from '#/utils/utils';
import {TransactionType, getMinReceivedTokenAmount} from '@shareland/shareland-common';
import {FormEvent, FunctionComponent, useContext, useState} from 'react';
import {useTranslation} from 'react-i18next';

export const ActionMenuTradeSellTab: FunctionComponent<DefaultChildren> = () => (
  <ActionMenuTradeMarketLimitTabs>
    {{
      market: <Market />,
      limit: <Limit />,
    }}
  </ActionMenuTradeMarketLimitTabs>
);

const Market: FunctionComponent<DefaultChildren> = () => {
  const transactionType = TransactionType.Sell;

  // Context
  const {landMarketInfo, landAccountInfo, initiateTransaction} = useContext(ActionMenuContext);

  // External hooks
  const [showInfoTile, onCloseTile] = useDismissableInfoTile(transactionType);

  // Internal hooks
  const actionMenuTokenInputData = useActionMenuTokenInputData({
    transactionType,
    initialSelectedKey: TokenType.LAND,
  });

  const [maxSlippagePercentage, setMaxSlippagePercentage] = useState(maxSlippageDefaultPercentage);

  // Vars
  const {landTokenAmount, stableTokenAmount} = actionMenuTokenInputData.tokenAmounts;
  const viewOnly = landAccountInfo.landTokenAmount === 0;
  const minReceivedStableAmount = getMinReceivedTokenAmount(
    stableTokenAmount,
    percentageToRatio(maxSlippagePercentage, true),
  );
  const transactionFee = getTransactionFees(stableTokenAmount);
  const receiveStableAmount = stableTokenAmount - transactionFee;
  const minReceivedStableAmountWithFee = minReceivedStableAmount - transactionFee;

  const transactionVars: TransactionVariables[typeof transactionType] = {
    landTokenAmount,
    stableTokenAmount,
    marketPrice: landMarketInfo.marketPrice,
    transactionFee,
    receiveStableAmount,
    maxSlippagePercentage,
    minReceivedStableAmount,
    minReceivedStableAmountWithFee,
  };

  const noticeMessages = useSellNoticeMessage(transactionVars, actionMenuTokenInputData);
  const canStartTransactionFlow = stableTokenAmount > 0 && noticeMessages.every(elem => elem.canSubmitForm);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (!canStartTransactionFlow) return;

    initiateTransaction({
      type: transactionType,
      data: transactionVars,
    });
  };

  return (
    <ActionMenuFormContainer onSubmit={onSubmit}>
      <ActionMenuLayoutContentNamed noticeMessages={noticeMessages}>
        {{
          info: showInfoTile && <ActionMenuInfoTile onClose={onCloseTile} tileKey={TransactionType.Sell} />,
          grow: (
            <ActionMenuTokenInput
              tokenInputData={actionMenuTokenInputData}
              viewOnly={viewOnly}
              maxButtonLandAmount={landAccountInfo.landTokenAmount}
            />
          ),
          overview: (
            <ActionMenuContentSectionOverviewSell
              className={viewOnly ? 'view-only' : ''}
              transactionVars={transactionVars}
              setMaxSlippagePercentage={setMaxSlippagePercentage}
            />
          ),
        }}
      </ActionMenuLayoutContentNamed>

      <ActionMenuLayoutFooter>
        <ActionMenuTransactionReviewButton
          className={viewOnly ? 'view-only' : ''}
          disabled={!canStartTransactionFlow}
        />
      </ActionMenuLayoutFooter>
    </ActionMenuFormContainer>
  );
};

const useSellNoticeMessage = (
  transactionVars: TransactionVariables[TransactionType.Sell],
  actionMenuTokenInputData: ReturnType<typeof useActionMenuTokenInputData>,
): MessageContent[] => {
  const {land, landMarketInfo, landAccountInfo} = useContext(ActionMenuContext);
  const {t} = useTranslation('translation');
  const {setTab} = useQueryParamsTabKey({queryParamTabKey: 'tradeTabId', tabKeys: TradeTab});

  const cases: Record<string, MessageCase> = {
    noLandTokenToSell: {
      condition: () => landAccountInfo.landTokenAmount === 0,
      message: () =>
        actionMenuMessageContent.needToBuyOrBorrowToSell(t, land.tokenSymbol, () => {
          setTab(TradeTab.BUY);
        }),
    },
    notEnoughLandTokenInWalletToSell: {
      condition: () => transactionVars.landTokenAmount > landAccountInfo.landTokenAmount,
      message: () => actionMenuMessageContent.notEnoughTokenAmount(t, landMarketInfo.symbol),
    },
    notEnoughLiquidity: {
      condition: () =>
        landMarketInfo.marketCollateralPositionLandTokenAmount < transactionVars.landTokenAmount &&
        landMarketInfo.totalLiquidity === 0,
      message: () => actionMenuMessageContent.notEnoughLiquidity(t),
    },
    maxSlippageTooLowTransactionLikelyToFail: {
      condition: () => transactionVars.maxSlippagePercentage < maxSlippageDefaultPercentage,
      message: () =>
        actionMenuMessageContent.transactionLikelyToFailMaxSlippageValueLower(t, maxSlippageDefaultPercentage),
    },
    transactionFeesHigherThanReceived: {
      condition: () => transactionVars.minReceivedStableAmount < transactionVars.transactionFee,
      message: () => actionMenuMessageContent.transactionFeesHigherThanReceivedAmount(t),
    },
  };

  return useMessageCasesToContent(cases, {
    inputValidationMessage: actionMenuTokenInputData.tokenInput.validationMessage,
  });
};

export const Limit: FunctionComponent<DefaultChildren> = () => {
  // Context
  const {landMarketInfo, landAccountInfo, initiateTransaction} = useContext(ActionMenuContext);

  // External hooks
  const [showInfoTile, onCloseTile] = useDismissableInfoTile(TransactionType.Sell);

  // Internal hooks
  const {price, amount, subtotal} = usePriceAmountSubtotal(landMarketInfo.marketPrice);

  // Vars
  const viewOnly = landAccountInfo.landTokenAmount === 0;
  const sellTransactionFee = getTransactionFees(subtotal.value);
  const transactionFee = minimumTransactionFees;
  const receiveStableAmount = subtotal.value - sellTransactionFee;

  const transactionVars: TransactionVariables[TransactionType.SellLimitOrder] = {
    landTokenAmount: Math.round(amount.value),
    stableTokenAmount: Math.round(subtotal.value),
    sellTransactionFee,
    transactionFee,
    receiveStableAmount,
    targetPrice: price.value,
  };

  const noticeMessages = useLimitTokenNoticeMessage(transactionVars);

  const canStartTransactionFlow =
    !!amount.value && !!subtotal.value && noticeMessages.every(elem => elem.canSubmitForm);

  // Functions
  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (!canStartTransactionFlow) return;

    initiateTransaction({
      type: TransactionType.SellLimitOrder,
      data: transactionVars,
    });
  };

  return (
    <ActionMenuFormContainer onSubmit={onSubmit}>
      <ActionMenuLayoutContentNamed noticeMessages={noticeMessages}>
        {{
          info: showInfoTile && <ActionMenuInfoTile onClose={onCloseTile} tileKey={TransactionType.Sell} />,
          grow: (
            <div className={`${viewOnly ? 'view-only' : ''} space-y-4`}>
              <LimitOrderTokenInputs
                type={TransactionType.SellLimitOrder}
                price={price}
                amount={amount}
                subtotal={subtotal}
              />
            </div>
          ),
          overview: (
            <ActionMenuContentSectionOverviewLimitSell
              className={viewOnly ? 'view-only' : ''}
              transactionVars={transactionVars}
            />
          ),
        }}
      </ActionMenuLayoutContentNamed>

      <ActionMenuLayoutFooter>
        <ActionMenuTransactionReviewButton disabled={!canStartTransactionFlow} />
      </ActionMenuLayoutFooter>
    </ActionMenuFormContainer>
  );
};

const useLimitTokenNoticeMessage = (transactionVars: TransactionVariables[TransactionType.SellLimitOrder]) => {
  const {land, landMarketInfo, landAccountInfo} = useContext(ActionMenuContext);
  const {t} = useTranslation('translation');
  const {setTab} = useQueryParamsTabKey({queryParamTabKey: 'tradeTabId', tabKeys: TradeTab});

  const cases: Record<string, MessageCase> = {
    noLandTokenToSell: {
      condition: () => landAccountInfo.landTokenAmount === 0,
      message: () =>
        actionMenuMessageContent.needToBuyOrBorrowToSell(t, land.tokenSymbol, () => {
          setTab(TradeTab.BUY);
        }),
    },
    notEnoughLandTokenInWalletToSell: {
      condition: () => transactionVars.landTokenAmount > landAccountInfo.landTokenAmount,
      message: () => actionMenuMessageContent.notEnoughTokenAmount(t, landMarketInfo.symbol),
    },
    notEnoughLiquidity: {
      condition: () =>
        landMarketInfo.marketCollateralPositionLandTokenAmount < transactionVars.landTokenAmount &&
        landMarketInfo.totalLiquidity === 0,
      message: () => actionMenuMessageContent.notEnoughLiquidity(t),
    },
    mightProcessAsMarketOrder: {
      condition: () =>
        !!transactionVars.landTokenAmount &&
        !!transactionVars.stableTokenAmount &&
        transactionVars.targetPrice <= landMarketInfo.marketPrice,
      message: () => actionMenuMessageContent.limitSellMightProcessAsMarketOrder(t),
    },
  };

  return useMessageCasesToContent(cases, {
    usdcAmount: transactionVars.stableTokenAmount,
  });
};
